import { useQuery } from 'react-query';
import toastr from 'react-hot-toast';
import { fetchWrapper } from 'utils/fetchWrapper';
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index';
import type { DropdownOption } from 'types/common/DropdownOption';
import React from 'react';
import type {
  SparkRunRecommendationsStatusTypeResponse,
  SparkRunRecommendationsStatusType,
} from 'types/entityTypes/SparkRunRecommendationsStatusTypes';

const getSparkRunRecommendationsStatusType = () => {
  return fetchWrapper<SparkRunRecommendationsStatusTypeResponse>({
    url: `sparkRunRecommendationStatusTypes`,
    method: 'GET',
  });
};

export function useSparkRunRecommendationsStatusTypes() {
  const { data, ...rest } = useQuery(
    'Spark Run Recommendation Status Types',
    getSparkRunRecommendationsStatusType,
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(
            `failed to Fetch Spark Run Recommendation Status Types: ${error.message}`
          );
        }
      },
      ...INFINITE_CACHE_AND_STALE_TIME,
    }
  );

  const { data: responseData, ...restData } = data || {};

  const idMap = responseData?.reduce<
    Record<string, SparkRunRecommendationsStatusType>
  >((acc, curr) => {
    return { ...acc, [curr.id]: curr };
  }, {});

  const sparkRunRecommendationStatusIdByNameMap = React.useMemo(() => {
    return responseData?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.attributes.name]: curr.id };
    }, {});
  }, [responseData]);

  const sparkRunRecommendationStatusNameByIdMap = React.useMemo(() => {
    return responseData?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.id]: curr.attributes.name };
    }, {});
  }, [responseData]);

  const dropdownOptions: DropdownOption[] =
    responseData?.map((type) => ({
      label: type.attributes.name,
      value: type.id,
    })) || [];

  return {
    sparkRunRecommendationStatusTypes: responseData || [],
    sparkRunRecommendationStatusIdByNameMap,
    sparkRunRecommendationStatusNameByIdMap,
    idMap,
    dropdownOptions,
    ...restData,
    ...rest,
  };
}
